import React from 'react';
import PropTypes from 'prop-types';
import { BannerHeader } from '../blocks/BannerHeader';
import { SuggestedQuestions } from '../blocks/SuggestedQuestions';
import { Summary } from '../blocks/Summary';

const BANNER_STYLE = { backgroundColor: '#FEF2E9' };

/** The column is two API questions closed by the ask-your-own tile, however many the API returns. */
const MAX_QUESTIONS = 2;

/**
 * The original banner: a peach panel with the full header above a questions column and a white
 * summary card.
 */
export const VariantA = ({ questions, summary, onAskQuestion }) => {
  const { title, body } = summary || {};

  return (
    <section
      id="ai-assisted-search-banner"
      data-component="AiAssistedSearchBanner"
      data-testid="ai-assisted-search-banner"
      data-variant="variant-a"
      aria-labelledby="ai-assisted-search-banner-title"
      style={BANNER_STYLE}
      // The results grid insets every pod by 4px a side, which the banner does not inherit, so it
      // hangs past the rows left and right and is left with only their 4px of clearance above and
      // below. `sui-mx-1` is that same 4px and `sui-my-3` adds to their 4px for the 16px the design
      // has. This card carries its own root rather than `GradientCard`'s, hence the repetition.
      className="sui-rounded-lg sui-p-6 lg:sui-p-8 sui-mx-1 sui-my-3"
    >
      <BannerHeader />
      <div
        id="ai-assisted-search-banner-body"
        data-testid="ai-assisted-search-banner-body"
        className="sui-flex sui-flex-col lg:sui-flex-row sui-gap-4 lg:sui-gap-6 sui-mt-6"
      >
        <div
          id="ai-assisted-search-banner-suggested-questions"
          data-testid="ai-assisted-search-banner-suggested-questions"
          className="lg:sui-basis-1/3 lg:sui-shrink-0"
        >
          <SuggestedQuestions
            questions={questions}
            maxQuestions={MAX_QUESTIONS}
            onAskQuestion={onAskQuestion}
          />
        </div>
        <div
          id="ai-assisted-search-banner-summary"
          data-testid="ai-assisted-search-banner-summary"
          className="lg:sui-basis-2/3 lg:sui-grow sui-min-w-0"
        >
          <Summary title={title} body={body} />
        </div>
      </div>
    </section>
  );
};

VariantA.displayName = 'VariantA';

VariantA.propTypes = {
  /** Questions for the left column. `text` is markdown. */
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.number.isRequired,
      text: PropTypes.string,
    })
  ),
  /** Called when a question is chosen. */
  onAskQuestion: PropTypes.func,
  /** `Summary` for the right column. `body` is markdown. */
  summary: PropTypes.shape({
    type: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.string,
  }),
};
